import api from "@/lib/axiosConfig";
import { Trash } from "@/lib/types";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

interface GetTrashData {
  where: any;
  orderBy: any;
  take: number;
  skip: number;
}

async function getTrash(data: GetTrashData) {
  const response = await api.post("/back/api/trash/get", data);
  return response.data;
}

export const useGetTrash = ({ where, orderBy, take, skip }: GetTrashData) => {
  return useQuery({
    queryKey: ["trash", { where, orderBy, take, skip }],
    queryFn: () => getTrash({ where, orderBy, take, skip }),
    placeholderData: keepPreviousData,
  });
};

async function addToTrash(data: Trash) {
  const response = await api.post("/back/api/trash", {
    data: data,
  });
  return response.data;
}

export const useAddToTrash = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: addToTrash,
    onSuccess: (_, v) => {
      if (v.documentType === "artwork") {
        queryClient.invalidateQueries({ queryKey: ["artworks", v.documentId] });
      } else if (v.documentType === "tirage") {
        queryClient.invalidateQueries({ queryKey: ["tirage", v.documentId] });
        queryClient.invalidateQueries({ queryKey: ["tirages"], exact: false });
        queryClient.invalidateQueries({ queryKey: ["countByStatus", "tirage"], exact: false });
      } else {
        queryClient.invalidateQueries({
          queryKey: [v.documentType, v.documentId],
        });
      }
      queryClient.invalidateQueries({ queryKey: ["trash"] });
    },
  });
};

async function countTrash(where: any) {
  const response = await api.post("/back/api/trash/count", {
    where: where,
  });
  return response.data;
}

export const useCountTrash = (where: any) => {
  return useQuery({
    queryKey: ["trash", "count", where],
    queryFn: () => countTrash(where),
  });
};

async function removeFromTrash(where: any) {
  const response = await api.post("/back/api/trash/removeFromTrash", {
    where: where,
  });
  return response.data;
}

export const useRemoveFromTrash = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: removeFromTrash,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["trash"] });
    },
  });
};

async function deleteReelFromEverywhere(where: any) {
  const response = await api.post("/back/api/trash/delete", {
    where: where,
  });
  return response.data;
}

export const useDeleteTotal = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteReelFromEverywhere,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["trash"] });
    },
  });
};
